import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import HandicapSparkline from "../components/HandicapSparkline";
import BagStatsCard from "../components/BagStatsCard";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { ArrowLeft, SignOut, Check, Lock, Sparkle, Golf, PencilSimple, ChatCircleText, X, ShareNetwork, WhatsappLogo, Copy, Camera, Trash } from "@phosphor-icons/react";
import { resetWelcome } from "../components/WelcomeModal";

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const [method, setMethod] = useState("");
  const [handle, setHandle] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  // Handicap inline edit state
  const [hcpEditing, setHcpEditing] = useState(false);
  const [hcpInput, setHcpInput] = useState("");
  const [hcpSaving, setHcpSaving] = useState(false);
  const [hcpError, setHcpError] = useState("");
  // Change-password state
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);
  // WHS handicap history — populated on mount + after any round changes.
  // Shape: { current_index, auto_updated, updated_at, rounds: [...], counted_indices: [...] }
  const [hcpHistory, setHcpHistory] = useState(null);
  const [hcpHistoryLoading, setHcpHistoryLoading] = useState(false);
  const [hcpHistoryOpen, setHcpHistoryOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setMethod(user.payment_method || "");
      setHandle(user.payment_handle || "");
    }
  }, [user]);

  // Load the WHS handicap history once the user is available. Re-fetched
  // after any manual handicap change so the "auto-updated" badge stays
  // in sync with the server view.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      setHcpHistoryLoading(true);
      try {
        const { data } = await api.get("/auth/me/handicap-history");
        if (!cancelled) setHcpHistory(data);
      } catch {
        // Non-fatal — just show the current handicap without history.
      } finally {
        if (!cancelled) setHcpHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.handicap]);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    setSaved(false);
    try {
      await api.patch("/auth/me/payment", {
        payment_method: method || null,
        payment_handle: handle.trim() || null,
      });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Save / clear handicap inline. Empty string clears it on the backend.
  const saveHandicap = async () => {
    setHcpError("");
    setHcpSaving(true);
    try {
      const raw = hcpInput.trim();
      await api.patch("/auth/me/handicap", { handicap: raw === "" ? null : raw });
      await refresh();
      setHcpEditing(false);
    } catch (err) {
      setHcpError(formatApiError(err));
    } finally {
      setHcpSaving(false);
    }
  };

  const placeholder = {
    paypal: "yourpaypalname (paypal.me/yourpaypalname)",
    monzo: "yourmonzoname (monzo.me/yourmonzoname)",
    revolut: "yourrevolut (revolut.me/yourrevolut)",
  }[method] || "Your payment handle";

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (newPw.length < 6) { setPwError("Password needs at least 6 characters."); return; }
    if (newPw !== confirmPw) { setPwError("Passwords don't match."); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/me/change-password", { current_password: curPw, new_password: newPw });
      setPwSaved(true);
      setCurPw(""); setNewPw(""); setConfirmPw("");
      setTimeout(() => { setPwSaved(false); setPwOpen(false); }, 1500);
    } catch (err) {
      setPwError(formatApiError(err));
    } finally {
      setPwSaving(false);
    }
  };

  const isPro =
    user?.role === "admin" ||
    ["active", "trialing"].includes(user?.subscription_status);

  return (
    <div className="px-5 pt-6 pb-8">
      <Link to="/app" className="btn-ghost -ml-2 mb-3" data-testid="profile-back-link"><ArrowLeft size={20} weight="bold" /></Link>

      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mb-5">Profile</h1>

      <div className="surface-card p-6 mb-4 flex items-center gap-4">
        <AvatarUploader user={user} />
        <div className="flex-1">
          <div className="font-heading font-bold text-xl text-[var(--primary)]" data-testid="profile-name">{user?.name}</div>
          <div className="text-sm text-[var(--text-secondary)]" data-testid="profile-email">{user?.email}</div>
          {/* Editable handicap row. Tap the pencil to swap in an inline input
              + Save/Cancel — keeps the page chrome-free while letting golfers
              tweak their handicap after a good or bad round. */}
          <div className="mt-1.5 flex items-center gap-2" data-testid="profile-handicap-row">
            {hcpEditing ? (
              <>
                <span className="text-sm text-[var(--text-secondary)]">Handicap</span>
                <input
                  type="number"
                  step="0.1"
                  min={-10}
                  max={54}
                  value={hcpInput}
                  onChange={(e) => setHcpInput(e.target.value)}
                  placeholder="e.g. 14.2"
                  disabled={hcpSaving}
                  data-testid="profile-handicap-input"
                  className="w-20 px-2 py-1 rounded-lg border border-[var(--border-default)] font-mono-num text-sm focus:outline-none focus:border-[var(--primary)]"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={saveHandicap}
                  disabled={hcpSaving}
                  data-testid="profile-handicap-save"
                  className="text-xs font-bold text-[var(--primary)] hover:underline disabled:opacity-50"
                >
                  {hcpSaving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => { setHcpEditing(false); setHcpError(""); }}
                  disabled={hcpSaving}
                  data-testid="profile-handicap-cancel"
                  className="text-xs text-[var(--text-secondary)] hover:underline disabled:opacity-50"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span className="text-sm text-[var(--text-secondary)]">
                  Handicap Index{" "}
                  {user?.handicap != null ? (
                    <span className="font-mono-num font-bold text-[var(--primary)]" data-testid="profile-handicap-value">
                      {user.handicap}
                    </span>
                  ) : (
                    <span className="italic opacity-60">not set</span>
                  )}
                  {hcpHistory?.auto_updated && user?.handicap != null && (
                    <span
                      className="ml-1.5 px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 text-[9px] uppercase tracking-wider font-bold"
                      data-testid="profile-handicap-auto-badge"
                      title="Automatically updated from your recent rounds using the WHS formula"
                    >
                      Auto · WHS
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setHcpInput(user?.handicap != null ? String(user.handicap) : "");
                    setHcpEditing(true);
                    setHcpError("");
                  }}
                  data-testid="profile-handicap-edit"
                  className="inline-flex items-center gap-1 text-xs font-bold text-[var(--primary)] hover:underline"
                  aria-label="Edit handicap"
                >
                  <PencilSimple size={12} weight="bold" />
                  {user?.handicap != null ? "Edit" : "Set"}
                </button>
              </>
            )}
          </div>
          {hcpError && (
            <div className="text-xs text-[var(--accent-terracotta)] mt-1" data-testid="profile-handicap-error">
              {hcpError}
            </div>
          )}
          {/* WHS Handicap Index history — shows every handicap-eligible
              round and bolds the ones currently counting toward the
              running index. Uses the "best N of last 20" WHS rule. */}
          {hcpHistory && hcpHistory.rounds && hcpHistory.rounds.length > 0 && (
            <div className="mt-3" data-testid="handicap-history-card">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setHcpHistoryOpen((v) => !v)}
                  className="text-xs font-bold text-[var(--primary)] hover:underline flex items-center gap-1"
                  data-testid="handicap-history-toggle"
                >
                  {hcpHistoryOpen ? "▾" : "▸"} WHS History ({hcpHistory.rounds.length} eligible round{hcpHistory.rounds.length === 1 ? "" : "s"})
                </button>
                <Link
                  to="/app/leaderboard"
                  className="text-xs font-bold text-[var(--primary)] hover:underline ml-auto"
                  data-testid="profile-leaderboard-link"
                >
                  Club Board →
                </Link>
              </div>
              {hcpHistoryOpen && (
                <div className="mt-2 border border-white/5 rounded-xl p-3 space-y-2" data-testid="handicap-history-body">
                  {/* Trend sparkline — reads the same rounds list. Oldest
                      first so time flows left-to-right. Improving trend
                      shows as a downward line (lower diffs = better). The
                      sparkline component handles its own empty state when
                      fewer than 2 rounds exist. */}
                  <div className="mb-2">
                    <HandicapSparkline
                      diffs={[...hcpHistory.rounds].reverse().map((r) => r.score_differential)}
                      height={42}
                      data-testid="profile-handicap-sparkline"
                    />
                  </div>
                  <div className="text-[10px] text-[var(--text-secondary)] italic mb-1">
                    WHS uses the best {hcpHistory.counted_indices.length || 0} score differential
                    {(hcpHistory.counted_indices.length || 0) === 1 ? "" : "s"} from your last {Math.min(20, hcpHistory.rounds.length)} rounds. Counted rounds are highlighted.
                  </div>
                  <div
                    className="grid gap-1 items-center text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider"
                    style={{ gridTemplateColumns: "1fr 44px 44px 42px" }}
                  >
                    <span>Course</span>
                    <span className="text-right">AGS</span>
                    <span className="text-right">CH</span>
                    <span className="text-right">Diff</span>
                  </div>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {hcpHistory.rounds.map((r) => (
                      <div
                        key={r.id}
                        data-testid={`handicap-history-row-${r.id}`}
                        className={`grid gap-1 items-center text-xs py-1 px-1.5 rounded ${
                          r.counts_toward_index ? "bg-green-500/10 font-bold" : ""
                        }`}
                        style={{ gridTemplateColumns: "1fr 44px 44px 42px" }}
                      >
                        <span className="truncate" title={r.course_name}>
                          {r.counts_toward_index && <span className="text-green-400 mr-1">●</span>}
                          {r.course_name}
                        </span>
                        <span className="text-right font-mono-num">{r.adjusted_gross ?? "—"}</span>
                        <span className="text-right font-mono-num opacity-70">{r.applied_course_handicap ?? "—"}</span>
                        <span className={`text-right font-mono-num ${r.counts_toward_index ? "text-green-300" : ""}`}>
                          {r.score_differential != null ? r.score_differential.toFixed(1) : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                  {hcpHistory.updated_at && (
                    <div className="text-[10px] text-[var(--text-secondary)] italic pt-1 border-t border-white/5">
                      Last auto-update: {new Date(hcpHistory.updated_at).toLocaleString("en-GB")}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {hcpHistoryLoading && !hcpHistory && (
            <div className="text-[10px] italic text-[var(--text-secondary)] mt-1">
              Loading round history…
            </div>
          )}
        </div>
      </div>

      {/* GPS shot tracker — per-club averages built from every logged
          shot. Rendered above the payment section so mates see their real
          bag come to life after each round. */}
      <BagStatsCard />

      {/* Payment handle */}
      <form onSubmit={save} className="surface-card p-5 mb-4 space-y-4" data-testid="payment-form">
        <div>
          <h3 className="font-heading font-bold text-lg text-[var(--primary)]">Payment handle</h3>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            Add this so league admins can pay you out fast when you win. We just open the app's deep link — no money flows through Fairway.
          </p>
        </div>

        <div>
          <label className="label-uppercase block mb-2">Preferred app</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: "paypal", label: "PayPal.me" },
              { id: "monzo", label: "Monzo.me" },
              { id: "revolut", label: "Revolut.me" },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMethod(m.id)}
                data-testid={`pay-method-${m.id}`}
                className={`rounded-2xl py-3 text-sm font-heading font-bold border-2 transition-colors ${
                  method === m.id
                    ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                    : "bg-white text-[var(--primary)] border-[var(--border-default)]"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {method && (
          <div>
            <label className="label-uppercase block mb-2">Your handle</label>
            <input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              className="input-field"
              placeholder={placeholder}
              data-testid="pay-handle-input"
            />
            <p className="text-[11px] text-[var(--text-secondary)] mt-1">Just the username — we add the URL prefix.</p>
          </div>
        )}

        {error && <div className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}

        <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="save-payment-button">
          {saved ? <><Check size={18} weight="bold" /> Saved</> : (saving ? "Saving..." : "Save payment handle")}
        </button>
      </form>

      <p className="label-uppercase mt-6 mb-2" data-testid="profile-coaching-header">Coaching</p>

      {/* Swing Lab — Pro only. Record + analyse swings with frame-by-frame
          slow-mo and tempo measurement. */}
      <Link
        to="/app/swing-lab"
        data-testid={isPro ? "profile-swing-lab-link" : "profile-swing-lab-locked-link"}
        className="block surface-card p-4 mb-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: isPro ? "var(--accent-terracotta)" : "var(--accent-sand)",
              color: isPro ? "#F2EEE7" : "var(--primary)",
            }}
          >
            <Camera size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <div className="font-heading font-bold text-[var(--primary)] text-sm">Swing Lab</div>
              {!isPro && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[var(--accent-terracotta)]">
                  <Lock size={10} weight="bold" /> Pro
                </span>
              )}
              {isPro && (
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full" style={{ background: "var(--accent-turf)", color: "var(--primary)" }}>
                  New
                </span>
              )}
            </div>
            <div className="text-xs text-[var(--text-secondary)] truncate">
              {isPro
                ? "Record your swing, scrub frame-by-frame, measure tempo"
                : "Record + analyse your swing — Pro only"}
            </div>
          </div>
          <div className="text-[var(--text-secondary)] text-xs font-bold uppercase tracking-wider">›</div>
        </div>
      </Link>

      {/* Club bag — Pro only. Free users see a locked tile that links to /subscribe. */}
      {isPro ? (
        <Link
          to="/app/profile/clubs"
          data-testid="profile-clubs-link"
          className="block surface-card p-4 mb-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "var(--accent-turf)", color: "var(--primary)" }}
            >
              <Golf size={20} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-[var(--primary)] text-sm">Your club bag</div>
              <div className="text-xs text-[var(--text-secondary)] truncate">
                Set average yardages to get in-round club suggestions
              </div>
            </div>
            <div className="text-[var(--text-secondary)] text-xs font-bold uppercase tracking-wider">›</div>
          </div>
        </Link>
      ) : (
        <Link
          to="/subscribe"
          data-testid="profile-clubs-locked-link"
          className="block surface-card p-4 mb-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
            >
              <Golf size={20} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <div className="font-heading font-bold text-[var(--primary)] text-sm">Your club bag</div>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[var(--accent-terracotta)]">
                  <Lock size={10} weight="bold" /> Pro
                </span>
              </div>
              <div className="text-xs text-[var(--text-secondary)] truncate">
                Go Pro to save your yardages and unlock smart club suggestions
              </div>
            </div>
            <div className="text-[var(--text-secondary)] text-xs font-bold uppercase tracking-wider">›</div>
          </div>
        </Link>
      )}

      <WatchPinCard user={user} />

      <Link
        to="/app/subscribe"
        data-testid="profile-subscribe-link"
        className="block surface-card p-4 mb-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: isPro ? "var(--accent-terracotta)" : "var(--accent-sand)", color: isPro ? "#F2EEE7" : "var(--primary)" }}
          >
            <Sparkle size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-[var(--primary)] text-sm">
              {isPro ? "You're on Fairway Pro" : "Go Pro"}
            </div>
            <div className="text-xs text-[var(--text-secondary)] truncate">
              {isPro
                ? user?.subscription_current_period_end
                  ? `Renews ${new Date(user.subscription_current_period_end).toLocaleDateString("en-GB")}`
                  : "Active subscription"
                : "Unlock the full experience"}
            </div>
          </div>
          <div className="text-[var(--text-secondary)] text-xs font-bold uppercase tracking-wider">›</div>
        </div>
      </Link>

      {/* What you get with Pro — only show to free users so it's an upsell
          they can actually act on. Hidden once they're already Pro. */}
      {!isPro && (
        <div className="surface-card p-5 mb-4" data-testid="pro-perks-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={18} weight="fill" className="text-[var(--accent-terracotta)]" />
            <h3 className="font-heading font-bold text-[var(--primary)]">What you get with Pro</h3>
          </div>
          <ul className="space-y-1.5 text-sm">
            {[
              "Unlimited rounds (free is capped at 2)",
              "Every tee colour (free is yellow only)",
              "Leagues — create, join, money pot",
              "Smartwatch view + offline rounds",
              "Your club bag yardages saved",
              "Unlimited favourite courses (free caps at 5)",
              "Branded shareable fourball recaps",
              "Course scorecard scanner (AI vision)",
            ].map((perk) => (
              <li key={perk} className="flex items-start gap-2">
                <Check size={14} weight="bold" className="text-[var(--primary)] shrink-0 mt-0.5" />
                <span className="text-[var(--text)]">{perk}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/subscribe"
            data-testid="pro-perks-cta"
            className="mt-4 block text-center btn-primary text-sm"
          >
            Go Pro
          </Link>
        </div>
      )}

      {/* Change password */}
      <div className="surface-card p-5 mb-4">
        <button
          type="button"
          onClick={() => setPwOpen((v) => !v)}
          className="flex items-center justify-between w-full text-left"
          data-testid="profile-change-password-toggle"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
            >
              <Lock size={18} weight="bold" />
            </div>
            <div>
              <div className="font-heading font-bold text-[var(--primary)] text-sm">Change password</div>
              <div className="text-xs text-[var(--text-secondary)]">Rotate your password whenever you want</div>
            </div>
          </div>
          <span className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">{pwOpen ? "Close" : "Open"}</span>
        </button>

        {pwOpen && (
          <form onSubmit={changePassword} className="mt-4 space-y-3" data-testid="change-password-form">
            <div>
              <label className="label-uppercase block mb-2">Current password</label>
              <input
                type="password"
                value={curPw}
                onChange={(e) => setCurPw(e.target.value)}
                required
                className="input-field"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="label-uppercase block mb-2">New password</label>
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                minLength={6}
                className="input-field"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="label-uppercase block mb-2">Confirm new password</label>
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                minLength={6}
                className="input-field"
                data-testid="confirm-password-input"
              />
            </div>
            {pwError && <div className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="change-password-error">{pwError}</div>}
            <button type="submit" disabled={pwSaving} className="btn-primary w-full" data-testid="change-password-submit">
              {pwSaved ? <><Check size={18} weight="bold" /> Updated</> : pwSaving ? "Updating..." : "Update password"}
            </button>
          </form>
        )}
      </div>

      <ShareFairwayCard user={user} />

      <GetInTouchCard />

      {/* Re-open the first-time welcome screen — handy if someone clears
          their browser and wants the tour back, or for showing a mate. */}
      <button
        type="button"
        onClick={() => { resetWelcome(); window.location.reload(); }}
        data-testid="profile-show-welcome"
        className="text-[11px] font-bold text-[var(--text-secondary)] hover:text-[var(--primary)] underline-offset-2 hover:underline mb-3"
      >
        Show the welcome tour again
      </button>

      <button onClick={logout} className="btn-secondary w-full" data-testid="logout-button">
        <SignOut size={18} weight="bold" /> Sign out
      </button>

      <div className="mt-6 pb-4 flex items-center justify-center gap-3 text-[11px] uppercase tracking-wider text-[var(--text-secondary)]">
        <Link to="/terms" data-testid="profile-terms-link" className="hover:text-[var(--primary)] transition-colors">
          Terms
        </Link>
        <span>·</span>
        <Link to="/privacy" data-testid="profile-privacy-link" className="hover:text-[var(--primary)] transition-colors">
          Privacy
        </Link>
        <span>·</span>
        <span>© {new Date().getFullYear()} Fairway</span>
      </div>
    </div>
  );
}


function SendWatchLinkButton() {
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const send = async () => {
    setErr("");
    setMsg("");
    setSending(true);
    try {
      const { data } = await api.post("/auth/me/send-watch-link");
      setMsg(`Sent to ${data?.sent_to || "your inbox"} — open it on your watch.`);
      setTimeout(() => setMsg(""), 6000);
    } catch (e) {
      // If the backend responded with JSON, show its detail. Otherwise the
      // request failed at the network/CDN level (Cloudflare 520/522 etc.)
      // — surface a plain-English hint instead of the raw error object.
      const detail = e?.response?.data?.detail;
      if (detail) {
        setErr(detail);
      } else if (e?.response?.status >= 500) {
        setErr("Server didn't respond cleanly — try again in a few seconds.");
      } else {
        setErr("Couldn't reach the server — check your connection and retry.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 border-t border-[var(--border-default)] pt-3" data-testid="watch-send-link">
      <button
        onClick={send}
        disabled={sending}
        data-testid="watch-send-link-btn"
        className="btn-secondary w-full text-sm flex items-center justify-center gap-2"
      >
        {sending ? "Sending…" : "📧 Email watch link to me"}
      </button>
      <p className="text-[10px] text-[var(--text-secondary)] mt-2 leading-snug">
        Best for watches without a camera (Garmin, Fitbit). Open the email on your watch&apos;s mail app and tap the button — zero typing.
      </p>
      {msg && <p className="text-xs text-[var(--status-good)] font-bold mt-2">{msg}</p>}
      {err && <p className="text-xs text-[var(--status-bad)] font-bold mt-2">{err}</p>}
    </div>
  );
}

function WatchPinCard({ user }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    setMsg("");
    if (!/^\d{4,8}$/.test(pin)) {
      setErr("PIN must be 4–8 digits");
      return;
    }
    if (pin !== confirm) {
      setErr("PINs don't match");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/me/watch-pin", { pin });
      setMsg("Watch PIN saved");
      setPin("");
      setConfirm("");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card p-4 mb-4" data-testid="watch-pin-card">
      <div className="font-heading font-bold text-[var(--primary)] text-sm mb-1">Watch PIN</div>
      <p className="text-xs text-[var(--text-secondary)] mb-3">
        Set a 4-digit PIN so you can quickly log into the watch URL on your Galaxy Watch.
      </p>
      <div className="flex gap-2 mb-2">
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="PIN"
          maxLength={8}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          data-testid="watch-pin-input"
          className="input-field flex-1 font-mono-num text-center"
          style={{ letterSpacing: 6 }}
        />
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="Confirm"
          maxLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
          data-testid="watch-pin-confirm"
          className="input-field flex-1 font-mono-num text-center"
          style={{ letterSpacing: 6 }}
        />
      </div>
      <button
        onClick={save}
        disabled={saving || !pin || !confirm}
        data-testid="watch-pin-save"
        className="btn-primary w-full"
      >
        {saving ? "Saving…" : "Save Watch PIN"}
      </button>
      {msg && <p className="text-xs text-[var(--status-good)] font-bold mt-2">{msg}</p>}
      {err && <p className="text-xs text-[var(--status-bad)] font-bold mt-2">{err}</p>}
      <p className="text-[10px] text-[var(--text-secondary)] mt-3 leading-relaxed">
        On your watch, open <strong>fairway-golf.org/watch</strong>, enter your email + PIN, and your active round loads automatically.
      </p>

      {/* Email-to-self shortcut — for watches without a camera (Garmin S70,
          most fitness watches). User taps the button, then opens the email
          on their watch's mail app and taps the big "Open Fairway Watch"
          button — zero typing on the tiny watch keyboard. */}
      <SendWatchLinkButton />

      {/* QR-code shortcut — point the watch's camera (or open the watch's
          browser via NFC tag) at this code to skip typing the URL. Generated
          via the free api.qrserver.com so we don't need to add a QR library
          to the bundle. Always pre-fills the user's email so they only have
          to type the 4-digit PIN on the watch. */}
      <details className="mt-3 border-t border-[var(--border-default)] pt-3">
        <summary
          className="text-xs font-bold text-[var(--primary)] cursor-pointer select-none"
          data-testid="watch-qr-toggle"
        >
          Show QR code for quick setup
        </summary>
        <div className="mt-3 flex flex-col items-center gap-2">
          <img
            alt="QR code linking to the Fairway watch view"
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=${encodeURIComponent(
              `https://fairway-golf.org/watch?email=${encodeURIComponent((user?.email || "").trim())}`,
            )}`}
            className="rounded-lg shadow-sm bg-white p-1"
            width="200"
            height="200"
            data-testid="watch-qr-image"
          />
          <p className="text-[10px] text-[var(--text-secondary)] text-center leading-snug max-w-[260px]">
            <strong>1.</strong> On your watch, open the Camera app or browser scanner.<br />
            <strong>2.</strong> Scan this code — Fairway loads with your email pre-filled.<br />
            <strong>3.</strong> Enter your 4-digit PIN once, then tap &ldquo;Save link&rdquo; so you don&apos;t have to scan again.
          </p>
        </div>
      </details>
    </div>
  );
}

/**
 * "Share Fairway with mates" card — opens the native share sheet so the user
 * can fire off a one-tap invite via WhatsApp / iMessage / SMS / Instagram,
 * with a tastefully pre-written message. Falls back to a manual share modal
 * (WhatsApp deep link + copy-to-clipboard) on desktop / browsers without
 * Web Share API.
 *
 * Open to free + Pro users — this is a growth lever, not a Pro perk.
 */
function ShareFairwayCard({ user }) {
  const [fallback, setFallback] = useState(false);
  const [copied, setCopied] = useState(false);

  const inviteUrl = "https://fairway-golf.org";
  const firstName = (user?.name || "").split(" ")[0] || "your mate";
  const shareText =
    `${firstName} sent you Fairway — the free golf app that turns your phone into a GPS rangefinder, scorecard and fourball tracker for UK, Spain & Portugal courses.\n\nGrab it here: ${inviteUrl}`;

  const handleShare = async () => {
    // Prefer native share sheet when available (mobile Chrome/Safari, all iOS, modern Android).
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: "Try Fairway",
          text: shareText,
          url: inviteUrl,
        });
        return;
      } catch (err) {
        // User cancelled — silently no-op.
        if (err?.name === "AbortError") return;
        // Anything else → drop into fallback.
      }
    }
    setFallback(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* no clipboard permission — user can still hand-copy from the textarea */
    }
  };

  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  return (
    <>
      <button
        type="button"
        onClick={handleShare}
        data-testid="share-fairway-card"
        className="block w-full text-left surface-card p-4 mb-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: "var(--accent-terracotta)", color: "#F2EEE7" }}
          >
            <ShareNetwork size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-[var(--primary)] text-sm">Share Fairway with mates</div>
            <div className="text-xs text-[var(--text-secondary)] truncate">
              One-tap WhatsApp / iMessage invite
            </div>
          </div>
          <div className="text-[var(--text-secondary)] text-xs font-bold uppercase tracking-wider">›</div>
        </div>
      </button>

      {fallback && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={() => setFallback(false)}
          data-testid="share-fairway-fallback"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md surface-card p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[3px] font-bold text-[var(--text-secondary)]">Invite</div>
                <h3 className="font-heading font-bold text-xl text-[var(--primary)]">Share Fairway</h3>
              </div>
              <button
                type="button"
                onClick={() => setFallback(false)}
                aria-label="Close"
                data-testid="share-fairway-close"
                className="h-9 w-9 rounded-full flex items-center justify-center"
                style={{ background: "var(--bg-app)" }}
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            <p className="text-xs text-[var(--text-secondary)] mb-3">
              Native share isn&apos;t supported here — pick a channel:
            </p>

            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-fairway-whatsapp"
              className="btn-primary w-full mb-2"
              style={{ background: "#25D366" }}
            >
              <WhatsappLogo size={18} weight="fill" /> Send via WhatsApp
            </a>

            <button
              type="button"
              onClick={copyLink}
              data-testid="share-fairway-copy"
              className="btn-secondary w-full mb-3"
            >
              {copied ? (
                <><Check size={16} weight="bold" /> Copied!</>
              ) : (
                <><Copy size={16} weight="bold" /> Copy invite text</>
              )}
            </button>

            <div
              className="rounded-2xl p-3 text-[13px] whitespace-pre-wrap"
              style={{ background: "var(--bg-app)", color: "var(--text)" }}
            >
              {shareText}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * "Get in touch" card + modal form. Sends a support ticket via
 * POST /api/support/contact, which persists it to MongoDB and emails every
 * admin via Resend. Designed for "I'm a player and something's broken"
 * support requests, not feature ideas (those have a Pro upsell path).
 */
function GetInTouchCard() {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [urgency, setUrgency] = useState("normal"); // urgent | normal | idea
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  // Auto-open when the Welcome modal's "Open the contact form" link fires
  // (URL becomes /app/profile?contact=1). Strip the query so a refresh
  // doesn't keep re-opening the modal.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("contact") === "1") {
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("contact");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/support/contact", { subject, message, urgency });
      setSent(true);
      setSubject("");
      setMessage("");
      setUrgency("normal");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    setOpen(false);
    // Reset success state when the modal closes so the next open is fresh.
    setTimeout(() => { setSent(false); setError(""); }, 250);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="get-in-touch-card"
        className="block w-full text-left surface-card p-4 mb-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
          >
            <ChatCircleText size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-[var(--primary)] text-sm">Get in touch</div>
            <div className="text-xs text-[var(--text-secondary)] truncate">
              Bug, suggestion or billing issue — drop us a line
            </div>
          </div>
          <div className="text-[var(--text-secondary)] text-xs font-bold uppercase tracking-wider">›</div>
        </div>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={close}
          data-testid="get-in-touch-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md surface-card p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[3px] font-bold text-[var(--text-secondary)]">Support</div>
                <h3 className="font-heading font-bold text-xl text-[var(--primary)]">Get in touch</h3>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                data-testid="get-in-touch-close"
                className="h-9 w-9 rounded-full flex items-center justify-center"
                style={{ background: "var(--bg-app)" }}
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {sent ? (
              <div data-testid="get-in-touch-success" className="text-center py-6">
                <div
                  className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-3"
                  style={{ background: "var(--accent-turf)", color: "var(--primary)" }}
                >
                  <Check size={28} weight="bold" />
                </div>
                <h4 className="font-heading font-bold text-lg text-[var(--primary)] mb-1">Message sent</h4>
                <p className="text-sm text-[var(--text-secondary)]">
                  Cheers — we&apos;ll reply to your account email as soon as we can (usually within a working day).
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="btn-primary w-full mt-5"
                  data-testid="get-in-touch-done"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="get-in-touch-form">
                <p className="text-xs text-[var(--text-secondary)]">
                  We&apos;ll see your account email automatically — no need to repeat it.
                </p>
                <div>
                  <label className="label-uppercase block mb-1.5">How urgent?</label>
                  <div className="grid grid-cols-3 gap-2" data-testid="get-in-touch-urgency">
                    {[
                      { id: "urgent", icon: "🚨", label: "Urgent", desc: "It's broken" },
                      { id: "normal", icon: "🤷", label: "Normal", desc: "Question / niggle" },
                      { id: "idea", icon: "💭", label: "Idea", desc: "Feature wish" },
                    ].map((u) => {
                      const active = urgency === u.id;
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setUrgency(u.id)}
                          data-testid={`get-in-touch-urgency-${u.id}`}
                          className="rounded-2xl p-2.5 text-center border-2 transition-colors"
                          style={{
                            background: active ? "var(--primary)" : "var(--bg-app)",
                            color: active ? "#F2EEE7" : "var(--text)",
                            borderColor: active ? "var(--primary)" : "var(--border)",
                          }}
                        >
                          <div className="text-lg leading-none mb-1">{u.icon}</div>
                          <div className="font-heading font-bold text-xs leading-tight">{u.label}</div>
                          <div className="text-[9px] uppercase tracking-wider opacity-75 mt-0.5">{u.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="label-uppercase block mb-1.5">Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={120}
                    required
                    minLength={3}
                    placeholder="e.g. Can't sync my round to my watch"
                    className="input-field"
                    data-testid="get-in-touch-subject"
                  />
                </div>
                <div>
                  <label className="label-uppercase block mb-1.5">Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={3000}
                    minLength={10}
                    required
                    rows={6}
                    placeholder="Tell us what happened, when, and which course / round if relevant."
                    className="input-field min-h-[140px] resize-none"
                    data-testid="get-in-touch-message"
                  />
                  <div className="text-[10px] text-[var(--text-secondary)] text-right mt-1">
                    {message.length} / 3000
                  </div>
                </div>
                {error && (
                  <div className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={sending || subject.trim().length < 3 || message.trim().length < 10}
                  className="btn-primary w-full"
                  data-testid="get-in-touch-submit"
                >
                  {sending ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}


/**
 * Profile picture uploader — tap the avatar to choose a photo; once set, the
 * image renders on the Profile card AND on every player tab when scoring a
 * fourball (so mates can see each other in the round leaderboard).
 *
 * Backend resizes to 256×256 JPEG ~25 KB before storing as a data URL on
 * the user doc. No CDN/storage dependency.
 */
function AvatarUploader({ user }) {
  const { refresh } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const pick = () => fileRef.current?.click();

  const onPicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("image", f);
      // Don't set Content-Type — the browser adds the correct multipart boundary.
      await api.post("/auth/me/avatar", form);
      if (refresh) await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      // Reset the input so picking the same file again still fires onChange.
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your profile picture?")) return;
    setUploading(true);
    try {
      await api.delete("/auth/me/avatar");
      if (refresh) await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const initial = (user?.name || "?").slice(0, 1).toUpperCase();
  const hasAvatar = !!user?.avatar_url;

  return (
    <div className="relative shrink-0">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onPicked}
        className="hidden"
        data-testid="profile-avatar-input"
      />
      <button
        type="button"
        onClick={pick}
        disabled={uploading}
        data-testid="profile-avatar-button"
        aria-label={hasAvatar ? "Change profile picture" : "Upload profile picture"}
        className="h-16 w-16 rounded-full overflow-hidden bg-[var(--primary)] text-white font-heading font-bold flex items-center justify-center text-2xl relative group"
        style={{ opacity: uploading ? 0.6 : 1 }}
      >
        {hasAvatar ? (
          <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
        ) : (
          initial
        )}
        <div
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: "rgba(0,0,0,0.4)" }}
        >
          <Camera size={20} weight="fill" />
        </div>
      </button>
      {hasAvatar && !uploading && (
        <button
          type="button"
          onClick={remove}
          data-testid="profile-avatar-remove"
          aria-label="Remove profile picture"
          className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full flex items-center justify-center shadow-md border-2 border-white"
          style={{ background: "var(--accent-terracotta)", color: "white" }}
        >
          <Trash size={11} weight="bold" />
        </button>
      )}
      {error && (
        <div className="absolute top-full mt-1 left-0 text-[10px] text-[var(--accent-terracotta)] whitespace-nowrap">
          {error}
        </div>
      )}
    </div>
  );
}
